"use client";

import React from "react";
import Image from "next/image";
import { tinaFieldSafe } from "@/utils";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel";
import { Card } from "./ui/card";
import { CardContent } from "./ui/card";
import { useIsEditing } from "./ui/page";
import { PencilIcon } from "lucide-react";
import { useGlobalData } from "./TinaContext";

interface Testimonial {
  image: string;
  quote: string;
  name: string;
  role: string;
}

interface TestimonialsCarouselProps {
  testimonials: Testimonial[];
  showGlobalButton?: boolean;
}

export default function TestimonialsCarousel({
  testimonials,
  showGlobalButton = true,
}: TestimonialsCarouselProps) {
  const [api, setApi] = React.useState<CarouselApi>();
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const globalData = useGlobalData();

  const { isEditing, isOpen } = useIsEditing();

  React.useEffect(() => {
    if (!api) return;

    const onSelect = () => {
      setSelectedIndex(api.selectedScrollSnap());
    };

    api.on("select", onSelect);
    onSelect();

    return () => {
      api.off("select", onSelect);
    };
  }, [api]);

  if (!testimonials || testimonials.length === 0) return null;

  return (
    <>
      {isEditing && isOpen && showGlobalButton && (
        <div
          className="absolute -top-12 right-8 flex items-center gap-2 text-sm px-2 py-1"
          data-tina-field={tinaFieldSafe(globalData, "testimonials")}
        >
          <PencilIcon className="w-4 h-4 text-blue-500" />
          Edit Global
        </div>
      )}
      <Carousel
        setApi={setApi}
        className="cursor-grab focus:cursor-grabbing active:cursor-grabbing [&>div]:!overflow-visible"
        opts={{
          align: "start",
          loop: false,
        }}
      >
        <CarouselContent className="cursor-grab focus:cursor-grabbing active:cursor-grabbing">
          {testimonials.map((testimonial, index) => (
            <CarouselItem key={index} className="md:basis-[90%] lg:basis-[90%]">
              <Card>
                <CardContent className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-6 md:gap-10 ">
                  <div className="flex-1 relative aspect-square flex-shrink-0 w-full max-w-[400px]">
                    <Image
                      src={testimonial.image || "/placeholder.png"}
                      alt={testimonial.name}
                      // fill
                      className="object-cover h-full w-full"
                      width={400}
                      height={400}
                      quality={100}
                      data-tina-field={tinaFieldSafe(testimonial, "image")}
                    />
                  </div>
                  <div className="flex-1 flex flex-col justify-between gap-4">
                    <p
                      className="!text-sm md:!text-lg text-muted-foreground md:line-clamp-7"
                      data-tina-field={tinaFieldSafe(testimonial, "quote")}
                    >
                      {testimonial.quote}
                    </p>
                    <div className="">
                      <h3
                        className="h5"
                        data-tina-field={tinaFieldSafe(testimonial, "name")}
                      >
                        {testimonial.name}
                      </h3>
                      <div
                        className="!text-sm text-muted-foreground"
                        data-tina-field={tinaFieldSafe(testimonial, "role")}
                      >
                        {testimonial.role}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </CarouselItem>
          ))}
        </CarouselContent>
        {testimonials.length > 1 && (
          <div className="relative mt-12 flex gap-6 items-center">
            <div className="flex gap-4">
              <CarouselPrevious className="relative !top-0 !left-0 !translate-x-0 !translate-y-0" />
              <CarouselNext className="relative !top-0 !right-0 !translate-x-0 !translate-y-0" />
            </div>
            <div className="font-mono">
              <span className="text-secondary-400 mr-2">
                {String(selectedIndex + 1).padStart(2, "0")}
              </span>
              / {String(testimonials.length).padStart(2, "0")}
            </div>
          </div>
        )}
      </Carousel>
    </>
  );
}
